/**
 * Google Consent Mode v2
 *
 * GTM still loads for every visitor, but analytics/advertising storage starts
 * out denied in GDPR countries, so no `_ga` or ad cookies are written before
 * the visitor answers the cookie banner. Tags run in cookieless mode until
 * then, instead of not running at all.
 *
 * Consent Mode does the geo check itself via its `region` parameter, which is
 * what keeps this synchronous: the defaults have to reach the dataLayer before
 * the container loads, and the GeoIP lookup the banner uses is async.
 */
import { GDPR_COUNTRIES } from '@link/share/constants';

type ConsentValue = 'granted' | 'denied';

/** The banner cookie types that map onto a Consent Mode signal. */
export type ConsentCookieType = 'analytics' | 'advertising';

interface ConsentParams {
  ad_storage?: ConsentValue;
  ad_user_data?: ConsentValue;
  ad_personalization?: ConsentValue;
  analytics_storage?: ConsentValue;
  region?: readonly string[];
  wait_for_update?: number;
}

type Gtag = (
  command: 'consent',
  arg: 'default' | 'update',
  params: ConsentParams
) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/** How long tags wait for the banner's answer before firing denied. */
const CONSENT_UPDATE_TIMEOUT = 500;

/** Where the Silktide banner keeps each cookie type's answer. */
const CONSENT_STORAGE_PREFIX = 'silktideCookieChoice_';

/**
 * The canonical gtag stub. GTM only recognizes a gtag command when the raw
 * `arguments` object reaches the dataLayer, so it must not be spread into an
 * array first.
 */
const gtag = function () {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
} as Gtag;

const consentParams = (
  cookieType: ConsentCookieType,
  value: ConsentValue
): ConsentParams =>
  cookieType === 'analytics'
    ? { analytics_storage: value }
    : {
        ad_storage: value,
        ad_user_data: value,
        ad_personalization: value
      };

/** The visitor's stored answer for one cookie type, or null if never asked. */
const readStoredConsent = (cookieType: ConsentCookieType): boolean | null => {
  const stored = localStorage.getItem(`${CONSENT_STORAGE_PREFIX}${cookieType}`);
  return stored === null ? null : stored === 'true';
};

/** Push one cookie type's answer to Consent Mode. */
export function updateConsent(
  cookieType: ConsentCookieType,
  granted: boolean
): void {
  gtag(
    'consent',
    'update',
    consentParams(cookieType, granted ? 'granted' : 'denied')
  );
}

// Denied in GDPR countries, granted everywhere else. The most specific
// `region` wins, so the two calls are order-independent. Shared by
// `initConsentMode` and `consentModeSnippet`, so the two cannot drift apart.
const CONSENT_DEFAULTS: readonly ConsentParams[] = [
  {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    region: GDPR_COUNTRIES,
    wait_for_update: CONSENT_UPDATE_TIMEOUT
  },
  {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted'
  }
];

const COOKIE_TYPES = ['analytics', 'advertising'] as const;

/**
 * Declare consent defaults. Must run before the GTM container loads —
 * `initGtm` calls it, since ES module imports are hoisted and app entrypoints
 * therefore cannot order this themselves.
 */
export function initConsentMode(): void {
  CONSENT_DEFAULTS.forEach(params => gtag('consent', 'default', params));

  // Replay a returning visitor's answer now, so their tags are not held for
  // `wait_for_update` on every page load while the banner script loads.
  COOKIE_TYPES.forEach(cookieType => {
    const stored = readStoredConsent(cookieType);
    if (stored !== null) {
      updateConsent(cookieType, stored);
    }
  });
}

/**
 * `initConsentMode` as inline JavaScript, for a document rendered on the
 * server.
 *
 * A server-rendered page puts the GTM loader in the HTML so it starts with the
 * page, which leaves no point at which bundled code could run first; the
 * defaults have to be in the same inline script, ahead of the loader. Built
 * from the same `CONSENT_DEFAULTS` and `consentParams` as the function above.
 * The stored-answer read is wrapped in try/catch: an inline script that throws
 * would also stop the GTM loader that follows it.
 */
export function consentModeSnippet(): string {
  const updates = Object.fromEntries(
    COOKIE_TYPES.map(cookieType => [
      cookieType,
      {
        granted: consentParams(cookieType, 'granted'),
        denied: consentParams(cookieType, 'denied')
      }
    ])
  );
  return (
    'window.dataLayer=window.dataLayer||[];' +
    'function gtag(){dataLayer.push(arguments);}' +
    CONSENT_DEFAULTS.map(
      params => `gtag('consent','default',${JSON.stringify(params)});`
    ).join('') +
    `var u=${JSON.stringify(updates)};` +
    'try{Object.keys(u).forEach(function(t){' +
    `var v=localStorage.getItem(${JSON.stringify(CONSENT_STORAGE_PREFIX)}+t);` +
    "if(v!==null){gtag('consent','update',u[t][v==='true'?'granted':'denied']);}" +
    '});}catch(e){}'
  );
}
